<template>
  <div class="search-view">
    <div class="container py-4">
      <h1 class="search-title mb-3">البحث</h1>
      
      <!-- Tab Bar -->
      <div class="search-tabs mb-3">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          :class="['tab-btn', { active: selectedTab === tab.code }]"
          @click="selectTab(tab)"
        >
          {{ tab.label }}
        </button>
      </div>

      <div class="search-form mb-4">
        <div class="input-group">
          <input
            v-model="searchQuery"
            type="text"
            class="form-control search-input"
            placeholder="ابحث عن مقالات، صور، فيديوهات..."
          />
          <button @click="handleSearch" class="btn btn-primary search-button">بحث</button>
        </div>
        <p v-if="errorMessage" class="text-danger mt-2">{{ errorMessage }}</p>
      </div>

      <div v-if="isLoading" class="text-center">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">جاري البحث...</span>
        </div>
      </div>

      <div v-else-if="searchResults.length === 0 && searchQuery" class="text-center">
        <p>لا توجد نتائج</p>
      </div>

      <div v-else class="row g-3" v-if="selectedTab == 1">
        <div v-for="result in searchResults" :key="result.id" class="col-sm-6 col-lg-4">
          <cardVue :article="result"/>
        </div>
      </div>
      <div v-if="selectedTab == 2" class="row g-3">
        <div v-for="result in searchResults" :key="result.id" class="col-sm-6 col-lg-4">
          <cardVue :media="result" :mediaType="result.mediaType"/>
        </div>
      </div>
      <div v-if="selectedTab == 3" class="row g-3">
        <div v-for="result in searchResults" :key="result.id" class="col-sm-6 col-lg-4">
          <cardVue :article="result"/>
        </div>
      </div>
      <Pagination 
        v-if="totalPages > 1"
        :totalPages="totalPages" 
        :currentPage="currentPage" 
        @page-changed="handlePageChange"
        class="mt-4" 
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import breadcrumb from '@/components/breadcrumb.vue';
import cardVue from '@/components/cardVue.vue';
import Pagination from '@/components/Pagination.vue';
import { ApiClient, SearchControllerApi } from "@/api/index.js";
import { setDocumentTitle, scrollToTopIfScrolled } from '@/utils/utils';
const router = useRouter();
const route = useRoute();

const apiClient = new ApiClient();
const searchApi = new SearchControllerApi(apiClient);

const headerImage = ref("https://i.imgur.com/cykExNN.png");
const searchQuery = ref('');
const searchResults = ref([]);
const isLoading = ref(false);
const currentPage = ref(1);
const totalPages = ref(1);
const pageSize = ref(9);
const errorMessage = ref('');

const tabs = [
  { label: 'المقالات', value: 'article',code:1 },
  { label: 'الوسائط', value: 'media',code:2 },
  { label: 'الصفحات الشخصية', value: 'particle',code:3 },
];
const selectedTab = ref(1);

const selectTab = (tab) => {
  selectedTab.value = tab.code;
  currentPage.value = 1;
  searchResults.value = [];
  totalPages.value = 1;
  // Update the URL with the selected tab
  router.replace({
    query: {
      ...route.query,
      tab: tab.code,
      page:1,
    }
  });
  if (searchQuery.value.length >= 3) {
    handleSearch();
  }
};

const sanitizeInput = (input) => {
  // Basic XSS and SQL injection filter
  return input.replace(/[<>'";&]/g, '');
};

const handleSearch = () => {
  const sanitizedQuery = sanitizeInput(searchQuery.value);
  if (sanitizedQuery.length >= 3) {
    errorMessage.value = '';
    fetchSearchResults(sanitizedQuery);
  } else if (sanitizedQuery.length > 0) {
    errorMessage.value = 'يجب أن يحتوي البحث على 3 أحرف على الأقل';
    searchResults.value = [];
    totalPages.value = 1;
  } else {
    errorMessage.value = '';
    searchResults.value = [];
    totalPages.value = 1;
  }
};

const fetchSearchResults = (query) => {
  isLoading.value = true;
  const lang = 1; // Assuming 1 is the language code for Arabic

  if (selectedTab.value == 1) {
    searchApi.searchArticles(currentPage.value - 1, pageSize.value, query, lang, (error, data) => {
      isLoading.value = false;
      if (error) {
     //   console.error("Error fetching articles:", error);
        searchResults.value = [];
        totalPages.value = 1;
      } else {
        searchResults.value = data.content.map(item => ({ ...item, type: 'article' }));
        totalPages.value = data.totalPages || 1;
      }
    });
  } else if (selectedTab.value == 2) {
    searchApi.searchMedia(currentPage.value - 1, pageSize.value, query, lang, (error, data) => {
      isLoading.value = false;
      if (error) {
     //   console.error("Error fetching media:", error);
        searchResults.value = [];
        totalPages.value = 1;
      } else {
        searchResults.value = data.content.map(item => ({ ...item, type: 'media' }));
        totalPages.value = data.totalPages || 1;
      }
    });
  } else if (selectedTab.value == 3) {
    searchApi.searchPArticles(currentPage.value - 1, pageSize.value, query, lang, 0, (error, data) => {
      isLoading.value = false;
      if (error) {
     //   console.error("Error fetching particles:", error);
        searchResults.value = [];
        totalPages.value = 1;
      } else {
        searchResults.value = data.content.map(item => ({ ...item, type: 'particle' }));
        totalPages.value = data.totalPages || 1;
      }
    });
  }
};

const handlePageChange = (page) => {
  currentPage.value = page;
  // Update the URL with the current page
  router.replace({
    query: {
      ...route.query,
      page: page,
    }
  });
  fetchSearchResults(sanitizeInput(searchQuery.value));
};

const getPageParam = (val) => {
  const n = parseInt(val, 10);
  return (Number.isNaN(n) || n < 1) ? 1 : n;
};

onMounted(() => {
  const queryParam = route.query.q;
  const tabParam = parseInt(route.query.tab) || 1;
  const pageParam = getPageParam(route.query.page);
  setDocumentTitle('البحث');
  scrollToTopIfScrolled();
  if (!queryParam) {
    router.push('/');
  } else {
    searchQuery.value = sanitizeInput(queryParam);
    selectedTab.value = tabParam;
    currentPage.value = pageParam;
    handleSearch();
  }
});

// Watch for changes in the route
watch(
  () => [route.query.q, route.query.tab, route.query.page],
  ([newQuery, newTab, newPage], [oldQuery, oldTab, oldPage]) => {
    if (!newQuery) {
      router.push('/');
    } else {
      searchQuery.value = sanitizeInput(newQuery);
      selectedTab.value = parseInt(newTab) || 1;
      const pageParam = getPageParam(newPage);
      if (currentPage.value !== pageParam) {
        currentPage.value = pageParam;
      }
      handleSearch();
    }
  }
);
</script>

<style scoped>
.search-view {
  background-color: var(--section-background);
  min-height: 100vh;
  margin-top: 84px;
}

.search-title {
  color: var(--scond-color);
  font-weight: 700;
  font-size: 1.75rem;
}

.search-tabs {
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
}
.tab-btn {
  background: none;
  border: none;
  outline: none;
  padding: 0.5rem 1.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--main-color);
  border-bottom: 2px solid transparent;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s;
}
.tab-btn.active {
  color: var(--scond-color);
  border-bottom: 2px solid var(--scond-color);
}
.tab-btn:hover:not(.active) {
  color: var(--scond-color);
  border-bottom: 2px solid var(--scond-color);
  background-color: rgba(241, 88, 39, 0.07); /* subtle highlight */
  transition: border-color 0.2s, color 0.2s, background-color 0.2s;
}

.search-form {
  max-width: 500px;
  margin: 0 auto;
}

.search-input {
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 1rem;
  border: 2px solid var(--main-color);
}

.search-input:focus {
  border-color: var(--scond-color);
  box-shadow: 0 0 0 0.2rem rgba(241, 88, 39, 0.25);
}

.search-button {
  border-top-left-radius: 20px;
  border-bottom-left-radius: 20px;
  border-top-right-radius: 0px;
  border-bottom-right-radius: 0px;
  background-color: var(--main-color);
  border-color: var(--main-color);
  color: #fff;
  transition: all 0.3s ease;
}

.search-button:hover,
.search-button:focus {
  background-color: var(--main-color, #005a8e);
  border-color: var(--main-color, #005a8e);
  color: #fff;
  opacity: 0.9;
}

.search-input {
  border-top-left-radius: 0px;
  border-bottom-left-radius: 0px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}
</style>
